import {
  Alert,
  Button,
  Divider,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
  theme,
} from "antd";
import {
  AuditOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  FileTextOutlined,
  FormOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { ProcessChoice, ProcessSection, ProcessWorkflow } from "../../api";
import { designerFormUrl } from "../../constants";
import ConditionBuilder from "./ConditionBuilder";
import WorkflowEditor from "./WorkflowEditor";
import type { FieldRef, Problem, Selection, StageDraft } from "./types";
import { problemsFor } from "./validation";
import {
  FILL_PERMISSION_LABEL,
  labelFor,
  SIGNATURE_KIND_LABEL,
  STAGE_KIND_HINT,
  STAGE_KIND_SHORT,
} from "./terms";

const { Text, Title } = Typography;

type Choices = {
  stage_kinds: ProcessChoice[];
  fill_permissions: ProcessChoice[];
  signature_kinds: ProcessChoice[];
  assignments: ProcessChoice[];
  workflow_types: ProcessChoice[];
  condition_ops: string[];
  condition_order_ops: string[];
  /** Which assignment modes read the org chart. Server-sent; see
   *  `main.assignment.DERIVED`. */
  derived_assignments?: string[];
};

type Props = {
  selection: Selection;
  stages: StageDraft[];
  sections: Record<number, ProcessSection>;
  workflows: ProcessWorkflow[];
  groups: Array<{ id: number; name: string }>;
  orgUnits?: Array<{ id: number; code: string; name: string; has_head: boolean }>;
  choices: Choices;
  fields: FieldRef[];
  problems: Problem[];
  onClose: () => void;
  onStageChange: (key: string, patch: Partial<StageDraft>, merge?: string) => void;
  onStageMove: (key: string, delta: number) => void;
  onStageRemove: (key: string) => void;
  onSectionChange: (
    formId: number,
    patch: Partial<ProcessSection>,
    merge?: string
  ) => void;
  onWorkflowChange: (id: number, patch: Partial<ProcessWorkflow>) => void;
  onWorkflowRemove: (id: number) => void;
  onAddWorkflow: (stageKey: string) => void;
  onCreateSection: (stageKey: string) => void;
};

/** A labelled row, so every setting in the panel lines up the same way. */
const Field = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ marginBottom: 4 }}>
      {hint ? (
        <Tooltip title={hint}>
          <Text
            type="secondary"
            style={{ fontSize: 12, borderBottom: "1px dotted" }}
          >
            {label}
          </Text>
        </Tooltip>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {label}
        </Text>
      )}
    </div>
    {children}
  </div>
);

/**
 * The settings for whatever is selected on the canvas.
 *
 * Docked immediately beside the canvas -- not floating over it, and not in a
 * column on the far side of the screen. The point of editing on a canvas is
 * watching the shape react: adding a rule to a step immediately draws its
 * bypass, and changing what a step is for immediately empties it. An inspector
 * a thousand pixels away breaks that link, a modal breaks it harder by hiding
 * the canvas, and floating over the canvas covered the nodes nearest the panel
 * -- which, on a step-heavy process, meant covering the step you had just
 * clicked to open it.
 *
 * Only one thing is editable at a time, on purpose. An earlier version put
 * every step's controls on screen at once, which made a five-step process an
 * unreadable wall of dropdowns.
 */
const StepInspector = (props: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { selection, choices } = props;

  const localProblems = (
    match:
      | { type: "stage"; key: string }
      | { type: "section" | "workflow"; id: number }
  ) => {
    const found = problemsFor(props.problems, match);
    if (found.length === 0) return null;
    return (
      <Space
        direction="vertical"
        size={6}
        style={{ width: "100%", marginBottom: 14 }}
      >
        {found.map((problem, i) => (
          <Alert
            key={i}
            type={problem.severity === "error" ? "error" : "warning"}
            showIcon
            message={t(problem.message, problem.params)}
          />
        ))}
      </Space>
    );
  };

  const header = (title: string, subtitle?: string, actions?: React.ReactNode) => (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        marginBottom: 12,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <Title level={5} style={{ margin: 0 }} ellipsis>
          {title}
        </Title>
        {subtitle && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {subtitle}
          </Text>
        )}
      </div>
      {actions}
      <Tooltip title={t("Close")}>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={props.onClose}
          aria-label={t("Close")}
        />
      </Tooltip>
    </div>
  );

  if (!selection) return null;

  /* ---------------------------------------------------------------- a step */
  if (selection.type === "stage") {
    const stage = props.stages.find((s) => s.key === selection.key);
    if (!stage) return null;
    const index = props.stages.findIndex((s) => s.key === stage.key);
    const isEntry = stage.kind === "DataEntry";

    // Sections and sign-offs cannot share a step, so switching what a step is
    // for empties it. Silently dropping four sections is not something to
    // discover after saving, so a step with contents asks first.
    const changeKind = (kind: string) => {
      if (kind === stage.kind) return;
      const emptied = stage.form_ids.length + stage.workflow_ids.length;
      const apply = () =>
        props.onStageChange(stage.key, {
          kind,
          form_ids: [],
          workflow_ids: [],
        });
      if (emptied === 0) return apply();
      Modal.confirm({
        title: t("Change what this step is for?"),
        content: t(
          "A step either holds sections people fill in or sign-offs people sign — never both. Everything in this one moves back to the tray, and you can put it back before saving."
        ),
        okText: t("Change it"),
        cancelText: t("Cancel"),
        onOk: apply,
      });
    };

    return (
      <div>
        {header(
          t("Step {{n}}", { n: index + 1 }),
          t(labelFor(STAGE_KIND_SHORT, stage.kind)),
          <Space size={0}>
            <Tooltip title={t("Move earlier")}>
              <Button
                size="small"
                type="text"
                icon={<UpOutlined />}
                disabled={index === 0}
                onClick={() => props.onStageMove(stage.key, -1)}
                aria-label={t("Move earlier")}
              />
            </Tooltip>
            <Tooltip title={t("Move later")}>
              <Button
                size="small"
                type="text"
                icon={<DownOutlined />}
                disabled={index === props.stages.length - 1}
                onClick={() => props.onStageMove(stage.key, 1)}
                aria-label={t("Move later")}
              />
            </Tooltip>
            <Popconfirm
              title={t("Remove this step?")}
              description={t(
                "Its sections and sign-offs go back to the tray. Cases already under way are not affected."
              )}
              okText={t("Remove")}
              cancelText={t("Cancel")}
              onConfirm={() => props.onStageRemove(stage.key)}
            >
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                aria-label={t("Remove this step")}
              />
            </Popconfirm>
          </Space>
        )}

        {localProblems({ type: "stage", key: stage.key })}

        <Field label={t("What is this step called?")}>
          <Input
            value={stage.name}
            status={stage.name.trim() ? undefined : "error"}
            placeholder={t("e.g. Prepare the job")}
            onChange={(e) =>
              props.onStageChange(
                stage.key,
                { name: e.target.value },
                // Typing is one undo step, not one per keystroke.
                `stage-name:${stage.key}`
              )
            }
          />
        </Field>

        <Field
          label={t("What happens here?")}
          hint={t(labelFor(STAGE_KIND_HINT, stage.kind))}
        >
          <Segmented
            block
            value={stage.kind}
            options={choices.stage_kinds.map((k) => ({
              value: k.value,
              label: t(labelFor(STAGE_KIND_SHORT, k.value)),
              icon:
                k.value === "DataEntry" ? <FileTextOutlined /> : <AuditOutlined />,
            }))}
            onChange={(v) => changeKind(String(v))}
          />
          <Text
            type="secondary"
            style={{ fontSize: 11, display: "block", marginTop: 6 }}
          >
            {t(labelFor(STAGE_KIND_HINT, stage.kind))}
          </Text>
        </Field>

        <Field
          label={t("How long should this step take?")}
          hint={t(
            "Work created by this step gets a deadline this many hours after it is asked for, and shows as overdue once it passes. Leave it empty for no deadline. Calendar hours, not working hours."
          )}
        >
          <InputNumber
            min={1}
            max={100000}
            style={{ width: "100%" }}
            value={stage.sla_hours ?? undefined}
            placeholder={t("No deadline")}
            addonAfter={t("hours")}
            onChange={(value) =>
              props.onStageChange(
                stage.key,
                { sla_hours: (value as number) ?? null },
                // One undo step for the whole number, not one per digit.
                `stage-sla:${stage.key}`
              )
            }
          />
          <Text
            type="secondary"
            style={{ fontSize: 11, display: "block", marginTop: 6 }}
          >
            {/* Said out loud because the alternative reading -- that a deadline
                stops the case -- is the wrong one, and an administrator setting
                one needs to know which. */}
            {t(
              "A deadline marks work late; it never blocks a case or moves it on by itself."
            )}
          </Text>
        </Field>

        <Field
          label={t("Does every case do this step?")}
          hint={t(
            "Leave it as 'always' and every case walks this step. Add a rule and cases that do not match skip it entirely — you will see the skip drawn on the canvas."
          )}
        >
          <ConditionBuilder
            // Selecting another step reuses this instance; the key drops any
            // half-written clause rather than carrying it onto the next step.
            key={stage.key}
            value={stage.condition}
            onChange={(condition) =>
              props.onStageChange(stage.key, { condition })
            }
            fields={props.fields}
            stageIndex={index}
            ops={choices.condition_ops}
            orderOps={choices.condition_order_ops}
          />
        </Field>

        <Divider style={{ margin: "8px 0 12px" }} />

        <Text type="secondary" style={{ fontSize: 12 }}>
          {/* Source strings are the keys here and carry no plural forms, so a
              count of one needs its own sentence rather than "1 sections". */}
          {isEntry
            ? stage.form_ids.length === 1
              ? t("1 section in this step. Drag it to another step on the canvas.")
              : t("{{count}} sections in this step. Drag them between steps on the canvas.", {
                  count: stage.form_ids.length,
                })
            : stage.workflow_ids.length === 1
            ? t("1 sign-off in this step. Drag it to another step on the canvas.")
            : t("{{count}} sign-offs in this step. Drag them between steps on the canvas.", {
                count: stage.workflow_ids.length,
              })}
        </Text>

        <div style={{ marginTop: 10 }}>
          {isEntry ? (
            <Button size="small" onClick={() => props.onCreateSection(stage.key)}>
              {t("Add a section here")}
            </Button>
          ) : (
            <Button size="small" onClick={() => props.onAddWorkflow(stage.key)}>
              {t("Add a sign-off here")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------- a section */
  if (selection.type === "section") {
    const section = props.sections[selection.id];
    if (!section) return null;
    const change = (patch: Partial<ProcessSection>, merge?: string) =>
      props.onSectionChange(section.id, patch, merge);
    const holder = props.stages.find((s) => s.form_ids.includes(section.id));

    return (
      <div>
        {header(
          t(section.title),
          holder
            ? t("In step {{n}} · {{name}}", {
                n: props.stages.indexOf(holder) + 1,
                name: holder.name.trim() || t("unnamed"),
              })
            : t("Not in any step yet")
        )}

        <Space size={8} style={{ marginBottom: 14 }} wrap>
          <Tag>{section.type}</Tag>
          <Button
            size="small"
            icon={<FormOutlined />}
            onClick={() => navigate(`/forms/${section.id}/design`)}
          >
            {t("Edit the questions")}
          </Button>
          <Tooltip
            title={t(
              "The original designer. Use it for anything the new one cannot edit yet."
            )}
          >
            <Button
              size="small"
              type="text"
              onClick={() => window.open(designerFormUrl(section.id), "_blank")}
            >
              {t("Classic")}
            </Button>
          </Tooltip>
        </Space>

        {localProblems({ type: "section", id: section.id })}

        <Field
          label={t("What is this section called?")}
          hint={t(
            "The heading people read on the case, and the name it is known by everywhere else. Renaming changes no answer already stored in it."
          )}
        >
          <Input
            value={section.title}
            status={section.title.trim() ? undefined : "error"}
            placeholder={t("e.g. General information")}
            onChange={(e) =>
              // Typing is one undo step, not one per keystroke -- same merge
              // key shape as a step's name.
              change({ title: e.target.value }, `section-title:${section.id}`)
            }
          />
        </Field>

        <Field
          label={t("Who fills this in?")}
          hint={t(
            "Ownership, not a lock: administrators can always fill any section."
          )}
        >
          <Select
            style={{ width: "100%" }}
            value={section.fill_permission}
            onChange={(v) =>
              change({
                fill_permission: v,
                fill_group: v === "Group" ? section.fill_group : null,
              })
            }
            options={choices.fill_permissions.map((p) => ({
              value: p.value,
              label: t(labelFor(FILL_PERMISSION_LABEL, p.value)),
            }))}
          />
        </Field>

        {section.fill_permission === "Group" && (
          <Field label={t("Which group?")}>
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              placeholder={t("Pick a group")}
              value={section.fill_group ?? undefined}
              status={section.fill_group ? undefined : "error"}
              onChange={(v) => change({ fill_group: v })}
              options={props.groups.map((g) => ({ value: g.id, label: g.name }))}
            />
          </Field>
        )}

        <Field
          label={t("Does submitting it take a signature?")}
          hint={t(
            "Anything but 'no signature' means the section cannot be submitted without one."
          )}
        >
          <Select
            style={{ width: "100%" }}
            value={section.signature_kind}
            onChange={(v) => change({ signature_kind: v })}
            options={choices.signature_kinds.map((k) => ({
              value: k.value,
              label: t(labelFor(SIGNATURE_KIND_LABEL, k.value)),
            }))}
          />
        </Field>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: 10,
            borderRadius: token.borderRadius,
            background: token.colorFillQuaternary,
          }}
        >
          <Switch
            checked={section.is_required}
            onChange={(v) => change({ is_required: v })}
          />
          <div>
            <div style={{ fontSize: 13 }}>{t("Must be filled in")}</div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {section.is_required
                ? t("The step cannot finish until this section is submitted.")
                : t("Optional — this section never holds the step up.")}
            </Text>
          </div>
        </div>

        {/* Deliberately below "Must be filled in", and worded to separate the
            two: optional means always offered and never blocking, a rule means
            not part of the case at all. Before this, asking one question only
            sometimes needed a whole step of its own, which put a box on the
            canvas that was really a question. */}
        <Field
          label={t("Is this section always asked for?")}
          hint={t(
            "Leave it as 'always' and every case fills it in. Add a rule and cases that do not match never see it — it is not shown, cannot be filled, and never holds the step up."
          )}
        >
          <ConditionBuilder
            key={section.id}
            value={section.condition ?? {}}
            onChange={(condition) => change({ condition })}
            fields={props.fields}
            stageIndex={holder ? props.stages.indexOf(holder) : -1}
            ops={choices.condition_ops}
            orderOps={choices.condition_order_ops}
          />
        </Field>
      </div>
    );
  }

  /* ------------------------------------------------------------ a sign-off */
  const workflow = props.workflows.find((w) => w.id === selection.id);
  if (!workflow) return null;
  const holder = props.stages.find((s) =>
    s.workflow_ids.includes(selection.id)
  );

  return (
    <div>
      {header(
        workflow.name?.trim() || t("Untitled sign-off"),
        holder
          ? t("In step {{n}} · {{name}}", {
              n: props.stages.indexOf(holder) + 1,
              name: holder.name.trim() || t("unnamed"),
            })
          : t("Not in any step yet")
      )}
      {localProblems({ type: "workflow", id: selection.id })}
      <WorkflowEditor
        workflow={workflow}
        onChange={(patch) => props.onWorkflowChange(selection.id, patch)}
        onRemove={() => props.onWorkflowRemove(selection.id)}
        assignments={choices.assignments}
        workflowTypes={choices.workflow_types}
        groups={props.groups}
        fields={props.fields}
        ops={choices.condition_ops}
        orderOps={choices.condition_order_ops}
        orgUnits={props.orgUnits}
        derivedAssignments={choices.derived_assignments}
        stageIndex={holder ? props.stages.indexOf(holder) : -1}
      />
    </div>
  );
};

export default StepInspector;
